import { useState } from "react";
import { BulletList, Button, Dialog, ErrorSummary, PortalPanelBody, Stack, TextField, Typography } from "../../design-system";
import { createAssignment } from "../../portal-auth";
import type { AssignmentCreated } from "../../portal-auth";
import { isRenewalSubmittedWriteRefusal } from "../../state/outcome";
import { trimAssigneeName, validateAssigneeFields } from "./assignee-fields";
import type { AssigneeFieldErrors } from "./assignee-fields";
import { renewalCaptureContent as content } from "./content";

export interface AssignDialogSection {
  /** The assignment key TG-2's routes store (`assignmentKeyFor`). */
  readonly sectionId: string;
  readonly title: string;
}

export interface AssignDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly sections: readonly AssignDialogSection[];
  readonly onAssigned: (result: AssignmentCreated, assigneeName: string, assigneeEmail: string) => void;
  /** A 401 from the route: the caller shows the session-ended view. */
  readonly onNotPermitted?: () => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-a7zv): the server refused the
   * assignment with 409 `renewal_submitted` -- the caller routes the
   * session to `/renewal-status` instead of this dialog's own generic
   * server-error panel. */
  readonly onRenewalSubmitted?: () => void;
}

const NAME_FIELD_ID = "assign-dialog-name";
const EMAIL_FIELD_ID = "assign-dialog-email";

/**
 * TG-6, AC-16: the "Assign to a colleague" dialog. The no-tick refusal is
 * the section list's own; this dialog opens only with at least one
 * section. Focus moves into the dialog on open and returns to the opening
 * control on close (MUI Dialog's own focus trap and restore, SC 2.4.3).
 */
export function AssignDialog({
  open,
  onClose,
  sections,
  onAssigned,
  onNotPermitted,
  onRenewalSubmitted,
}: Readonly<AssignDialogProps>) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<AssigneeFieldErrors | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);

  async function handleSubmit() {
    setServerError(undefined);
    const validation = validateAssigneeFields(name, email, NAME_FIELD_ID, EMAIL_FIELD_ID);
    if (validation.summary.length > 0) {
      setErrors(validation);
      return;
    }
    setErrors(null);
    setIsSubmitting(true);
    const result = await createAssignment({
      sectionIds: sections.map((section) => section.sectionId),
      assigneeName: trimAssigneeName(name),
      assigneeEmail: email.trim(),
    });
    setIsSubmitting(false);
    if (!result.ok) {
      if (result.error.code === "owner_capability_required" && onNotPermitted !== undefined) {
        onNotPermitted();
        return;
      }
      if (isRenewalSubmittedWriteRefusal(result.error.code) && onRenewalSubmitted !== undefined) {
        onRenewalSubmitted();
        return;
      }
      setServerError(content.serverErrorMessage);
      return;
    }
    onAssigned(result.value, trimAssigneeName(name), email.trim());
  }

  return (
    <Dialog open={open} title={content.assignDialogTitle} onClose={onClose} testId="assign-dialog">
      <Stack gap="sm">
        <Typography variant="body">{content.assignDialogIntro}</Typography>
        <BulletList
          testId="assign-dialog-sections"
          items={sections.map((section) => ({ key: section.sectionId, text: section.title }))}
        />
        {errors !== null && errors.summary.length > 0 ? (
          <ErrorSummary items={errors.summary} id="assign-dialog-errors" testId="assign-dialog-errors" />
        ) : null}
        {serverError !== undefined ? (
          <PortalPanelBody id="assign-dialog-server-error" expanded tone="error" title={content.serverErrorTitle} testId="assign-dialog-server-error">
            <Typography variant="body">{serverError}</Typography>
          </PortalPanelBody>
        ) : null}
        <TextField
          id={NAME_FIELD_ID}
          label={content.theirNameLabel}
          value={name}
          onChange={(value) => {
            setName(value);
            setErrors(null);
          }}
          autoComplete="off"
          error={errors?.nameError !== undefined}
          disabled={isSubmitting}
          {...(errors?.nameError !== undefined ? { errorText: errors.nameError } : {})}
        />
        <TextField
          id={EMAIL_FIELD_ID}
          label={content.theirEmailLabel}
          value={email}
          onChange={(value) => {
            setEmail(value);
            setErrors(null);
          }}
          type="email"
          autoComplete="off"
          error={errors?.emailError !== undefined}
          disabled={isSubmitting}
          {...(errors?.emailError !== undefined ? { errorText: errors.emailError } : {})}
        />
        <Stack direction="dialogActions" gap="sm">
          <Button emphasis="primary" onClick={() => void handleSubmit()} disabled={isSubmitting} testId="assign-dialog-submit">
            {content.assignDialogSubmitLabel}
          </Button>
          <Button emphasis="quiet" onClick={onClose} disabled={isSubmitting} testId="assign-dialog-cancel">
            {content.dialogCancelLabel}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
